import { useState, useEffect } from 'react';
import { get } from '../api/usersModule/httpClient';

/**
 * Custom hook for fetching SKU records data
 * @param {Object} params - Hook parameters
 * @param {string} params.reportType - Type of report to fetch
 * @param {Object} params.filters - Query filters for the API
 * @param {Object} params.pagination - Pagination parameters
 * @param {boolean} params.enabled - Whether to enable the query
 * @returns {Object} Query result with data, loading state, error, and refetch function
 */
export function useSkusRecords({
  reportType,
  filters = {},
  pagination = { page: 1, limit: 50 },
  enabled = true
}) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Build query string from filters and pagination
   */
  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append('reportType', reportType);

    // Add date range filters - use selectedInitialDate/selectedFinalDate with DD/MM/YYYY format
    if (filters.dateRange?.initialDate) {
      params.append('selectedInitialDate', filters.dateRange.initialDate);
    }
    if (filters.dateRange?.finalDate) {
      params.append('selectedFinalDate', filters.dateRange.finalDate);
    }

    // Add boolean filters
    if (filters.groupByNit !== undefined) {
      params.append('groupByNit', filters.groupByNit.toString());
    }
    if (filters.onlyActive !== undefined) {
      params.append('onlyActive', filters.onlyActive.toString());
    }

    // Add NIT filters
    if (filters.nitList && filters.nitList.length > 0) {
      // Backend expects nitList as comma-separated string
      params.append('nitList', filters.nitList.join(','));
    }

    // Add articleType filter
    if (filters.articleType && filters.articleType.length > 0) {
      params.append('articleType', filters.articleType.join(','));
    }

    // Add category filter
    if (filters.category && filters.category.length > 0) {
      params.append('category', filters.category.join(','));
    }

    // Always add Nit parameter (default to '1' for consolidated view)
    if (filters.nit && filters.nit !== '1') {
      params.append('Nit', filters.nit);
    } else {
      params.append('Nit', '1');
    }

    // Add sorting
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.order) {
      params.append('order', filters.order);
    }

    // Add pagination
    params.append('page', pagination.page.toString());
    params.append('limit', pagination.limit.toString());

    return params.toString();
  };

  /**
   * Fetch data from API
   */
  const fetchData = async () => {
    if (!enabled) {
      return;
    }

    // Require date range for most reports
    if (reportType === 'dailyInventoryDistribution' &&
        (!filters.dateRange?.initialDate || !filters.dateRange?.finalDate)) {
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const queryString = buildQueryString();
      const response = await get(`/skusRecords?${queryString}`);

      if (response && response.items) {
        setData(response);
      } else if (response && response.errorMessage) {
        setIsError(true);
        setError(new Error(response.errorMessage));
      } else {
        setData({ items: [], pagination: { total: 0 } });
      }
    } catch (err) {
      setIsError(true);
      setError(err);
      setData({ items: [], pagination: { total: 0 } });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refetch data
   */
  const refetch = () => {
    fetchData();
  };

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [
    reportType,
    enabled,
    pagination.page,
    pagination.limit,
    filters.dateRange?.initialDate,
    filters.dateRange?.finalDate,
    filters.groupByNit,
    filters.onlyActive,
    filters.nitList,
    filters.articleType,
    filters.category,
    filters.nit,
    filters.sortBy,
    filters.order
  ]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch
  };
}

export default useSkusRecords;
